"use client";

import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useTenantAuthFlow } from "@/features/auth/providers/tenant-auth-flow-provider";
import {
  AUTH_BOOTSTRAP_QUERY_KEY,
  AUTH_TENANT_ACCESS_RESOLVE_QUERY_KEY,
} from "@/features/auth/services/auth-query";

function isAuthQueryKey(queryKey: readonly unknown[]) {
  return queryKey[0] === "auth";
}

/**
 * Keeps already-mounted screens honest after permission changes.
 *
 * The bootstrap payload is the authoritative permission graph. When its server-computed revision changes,
 * React re-renders CASL immediately, but resource queries that carry viewerActions/permission-sensitive payloads
 * may still be warm in React Query. Invalidate them centrally so buttons, forms, sidebars and detail panes stop
 * depending on every feature remembering its own auth cache rules.
 */
export function AuthorizationRevisionSync() {
  const queryClient = useQueryClient();
  const { authMe, isAuthenticated, tenantAccess } = useTenantAuthFlow();
  const previousRevisionRef = React.useRef<string | null>(null);
  const previousScopeRef = React.useRef<string | null>(null);

  const authorizationRevision = authMe?.authorizationRevision ?? null;
  const tenantScope = React.useMemo(() => {
    const workspaceId = tenantAccess?.resolved.workspace?.id ?? null;
    const branchId = tenantAccess?.resolved.branch?.id ?? null;
    if (!workspaceId) return null;
    return `${workspaceId}:${branchId ?? "organization"}`;
  }, [tenantAccess?.resolved.branch?.id, tenantAccess?.resolved.workspace?.id]);

  React.useEffect(() => {
    if (!authorizationRevision) return;

    const previousRevision = previousRevisionRef.current;
    previousRevisionRef.current = authorizationRevision;

    if (!previousRevision || previousRevision === authorizationRevision) return;

    void queryClient.invalidateQueries({ queryKey: AUTH_TENANT_ACCESS_RESOLVE_QUERY_KEY });
    void queryClient.invalidateQueries({
      predicate: (query) => !isAuthQueryKey(query.queryKey),
    });
  }, [authorizationRevision, queryClient]);

  React.useEffect(() => {
    if (!tenantScope) return;

    const previousScope = previousScopeRef.current;
    previousScopeRef.current = tenantScope;

    if (!previousScope || previousScope === tenantScope) return;

    void queryClient.invalidateQueries({
      predicate: (query) => !isAuthQueryKey(query.queryKey),
    });
  }, [queryClient, tenantScope]);

  React.useEffect(() => {
    if (!isAuthenticated) return;

    const refetchAuthorization = () => {
      void queryClient.refetchQueries({
        queryKey: AUTH_BOOTSTRAP_QUERY_KEY,
        type: "active",
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      refetchAuthorization();
    };

    window.addEventListener("focus", refetchAuthorization);
    window.addEventListener("pageshow", refetchAuthorization);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", refetchAuthorization);
      window.removeEventListener("pageshow", refetchAuthorization);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, queryClient]);

  return null;
}
